import { Component, Inject, OnInit, Type } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../../components/shared/confirm-dialog/confirm-dialog.component';
import { WizardPlugin } from '../../../../../decorators/wizard-plugin';
import { CustomerAllocationData } from '../../../../../models/wizard-plugin-models/customer-allocation-data';
import { IPluginPreviewComponent } from '../../../../../interfaces/plugin-preview-component.interface';
import { IWizardPluginComponent } from '../../../../../interfaces/wizard-plugin-component.interface';
import { BasePluginData } from '../../../../../models/wizard-plugin-models/base-plugin-data';
import { TrainingScheduleData, TrainingSchedulesData } from '../../../../../models/wizard-plugin-models/training-schedules-data';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ClassesEntity } from '../../../../../models/entities/classes-entity';
import { Requisition, TrainingSchedule } from '../../../../../models/entities/requisition-model';
import { Router } from '@angular/router';
import { ErrorHandlerService } from '../../../../../decoration-base/error-handler.service';
import { COURSE_ENTITY_SERVICE_TOKEN, CLASSES_ENTITY_SERVICE_TOKEN, CUSTOMER_ENTITY_SERVICE_TOKEN, TRAINER_ENTITY_SERVICE_TOKEN, REQUISITION_ENTITY_SERVICE_TOKEN } from '../../../../../injection-tokens/entity-service-tokens';
import { IClassesEntityService } from '../../../../../interfaces/entities/classes-entity-service.interface';
import { ICourseEntityService } from '../../../../../interfaces/entities/course-entity-service.interface';
import { ICustomerEntityService } from '../../../../../interfaces/entities/customer-entity-service.interface';
import { IRequisitionEntityService } from '../../../../../interfaces/entities/requisition-entity-service-interface';
import { ITrainerEntityService } from '../../../../../interfaces/entities/trainer-entity-service.interface';
import { ResourceCreatorService } from '../../../../../services/resource-creator.service';
import { CustomerEntity } from '../../../../../models/entities/customer';
import { TrainerEntity } from '../../../../../models/entities/trainer';
import { ResourceType } from '../../../../../models/resource-meta-data';
import { TrainingSchedulesPreviewComponent } from './training-schedules-preview/training-schedules-preview.component';
import { CourseEntity } from '../../../../../models/entities/course';
import { Pair } from '../../../../../models/pair';
import { ResourceTypePair } from '../../../../../models/resource-type-pair';
import { LoginEntityService } from '../../../../../services/entity-services/login-entity.service';
import { Log, IShortLogger } from '../../../../../services/logger.service';

interface DateRange {
  id: string;
  startDate: Date;
  endDate: Date;
  startTime: string;
  endTime: string;
  title?: string;
}

type ResourceLite = {
  id: string;
  name: string;
  type: string;
};


@WizardPlugin({
  name: "Batch Details",
  optional: false,
  description: "Create Training Schedules with multiple sessions for customers",
  dataModelType: TrainingSchedulesData
})


@Component({
  selector: 'app-training-schedules-plugin',
  templateUrl: './training-schedules-plugin.component.html',
  styleUrl: './training-schedules-plugin.component.css'
})

export class TrainingSchedulesPluginComponent implements IWizardPluginComponent, OnInit {
  private readonly logger: IShortLogger<TrainingSchedulesPluginComponent> = Log.create(TrainingSchedulesPluginComponent);
  // public pluginData: CustomerAllocationData = new CustomerAllocationData();
  public pluginData: TrainingSchedulesData = new TrainingSchedulesData();

  /** Restrict date pickers to today or later */
  readonly minDate: Date = new Date();

  public startDateValid: boolean = true;
  public endDateValid: boolean = true;
  public removalDateValid: boolean = true;

  requisitionForm!: FormGroup;
  timeSelectionForm!: FormGroup;


  courseOptions: { id: string; name: string }[] = [
    //  { id: '5', name: 'GCP Learning' }
  ];
  deletionTypeOptions = ['Immediate', 'Scheduled', 'Manual'];

  activeTabIndex: number = 0;

  resourceTypes = [
    { value: 'vm', label: 'Virtual Machine' },
    { value: 'db', label: 'Cloud DB' },
    { value: 'app', label: 'App Service' },
    { value: 'acc', label: 'User Account' }
  ];

  resourceTemplatesByType: { [key: string]: ResourceLite[] } = {
    vm: [],
    db: [],
    app: [],
    acc: []
  };
  public customers: CustomerEntity[] = [];
  public trainers: TrainerEntity[] = [];

  // Chip input keys
  readonly separatorKeyCodes: number[] = [ENTER, COMMA];

  // Participant helpers
  getParticipantList(batchIdx: number): string[] {
    const val = (this.schedules.at(batchIdx) as FormGroup).get('participants')?.value || '';
    return val ? val.split(',').map((p: string) => p.trim()).filter((p: string) => p) : [];
  }

  addParticipantToBatch(batchIdx: number, event: MatChipInputEvent): void {
    const email = (event.value || '').trim();
    if (!email) return;
    const existing = this.getParticipantList(batchIdx);
    if (!existing.includes(email)) {
      const updated = [...existing, email].join(',');
      (this.schedules.at(batchIdx) as FormGroup).patchValue({ participants: updated });
    }
    event.chipInput?.clear();
  }

  removeParticipantFromBatch(batchIdx: number, email: string): void {
    const updated = this.getParticipantList(batchIdx).filter(p => p !== email).join(',');
    (this.schedules.at(batchIdx) as FormGroup).patchValue({ participants: updated });
  }

  // Batch helpers
  addBatch(): void {
    this.schedules.push(this.createScheduleGroup());
  }

  confirmRemoveBatch(index: number, event: Event): void {
    event.stopPropagation(); // prevent accordion toggle
    if (this.schedules.length <= 1) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: {
        title: 'Remove Batch',
        message: `Remove "${(this.schedules.at(index) as any).get('title')?.value || ('Batch ' + (index + 1))}"? This cannot be undone.`,
        confirmText: 'Remove',
        cancelText: 'Cancel',
        type: 'warning'
      } as ConfirmDialogData
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) this.schedules.removeAt(index);
    });
  }

  removeBatch(index: number): void {
    if (this.schedules.length > 1) {
      this.schedules.removeAt(index);
    }
  }

  // Date range FormArray helpers
  getDateRangesArray(batchIdx: number): FormArray {
    return (this.schedules.at(batchIdx) as FormGroup).get('dateRanges') as FormArray;
  }

  addDateRange(batchIdx: number): void {
    this.getDateRangesArray(batchIdx).push(this.createDateRangeGroup());
  }

  removeDateRange(batchIdx: number, rangeIdx: number): void {
    const arr = this.getDateRangesArray(batchIdx);
    if (arr.length > 1) arr.removeAt(rangeIdx);
  }

  private createDateRangeGroup(initial?: { startDate?: Date | null; endDate?: Date | null; startTime?: string; endTime?: string }): FormGroup {
    return this.fb.group({
      startDate: [initial?.startDate ?? null, Validators.required],
      endDate: [initial?.endDate ?? null, Validators.required],
      startTime: [initial?.startTime ?? '09:00', Validators.required],
      endTime: [initial?.endTime ?? '17:00', Validators.required],
    });
  }
  tabCalendarStates: {
    [tabIndex: number]: {
      currentDate: Date;
      calendarDates: { date: Date, isCurrentMonth: boolean }[];
      dateRanges: DateRange[];
      isPanelOpen: boolean
      isSelecting: boolean;
      selectionStart: Date | null;
      selectionEnd: Date | null;
      currentHoverDate: Date | null;
    }
  } = {};

  // Time modal state
  showTimeModal = false;
  public tempStartTime = '09:00';
  public tempEndTime = '17:00';
  public tempTitle = '';
  public tempDateRange: { start: Date, end: Date } | null = null;
  editingRange: DateRange | null = null;
  editingTabIndex: number = 0;

  // end

  // Calendar data for each schedule
  // scheduleCalendarData: ScheduleCalendarData[] = [];
  dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Data storage for each tab
  tabDateRanges: DateRange[][] = [[]]; // Array of arrays for each tab
  tabParticipants: string[][] = [[]]; // Array of arrays for each tab
  public resourceTemplates: Pair[] = [];

  defaultCustomCourseId: string = '-1'

  constructor(private fb: FormBuilder,
    private router: Router,
    private resourceCreatorService: ResourceCreatorService,
    private dialog: MatDialog,
    @Inject(COURSE_ENTITY_SERVICE_TOKEN) private courseService: ICourseEntityService,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private classService: IClassesEntityService,
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN) private customerService: ICustomerEntityService,
    @Inject(TRAINER_ENTITY_SERVICE_TOKEN) private trainerService: ITrainerEntityService,
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN) private reqService: IRequisitionEntityService,
    private errorHandler: ErrorHandlerService,
    private loginEntityService: LoginEntityService,
    private snackBar: MatSnackBar,
  ) { }

  async ngOnInit(): Promise<void> {
    // this.courseOptions = [...this.courseService.getAllCourses().map(course => ({
    //   id: course.id,
    //   name: course.name
    // })), ...this.courseOptions];

    this.courseOptions = [{ id: '-1', name: 'Custom Course' }
    ];
    // this.loadTrainers();
    // Initialize the form here where FormBuilder is guaranteed to be available
    this.createForm();

    // Initialize sections as collapsed
    this.initializeSectionsAsCollapsed();


    // if (!this.pluginData.removalOption) this.pluginData.removalOption = 'immediate';

    ['vm', 'db', 'app', 'acc'].forEach(async type => {
      this.resourceTemplatesByType[type] = (await this.resourceCreatorService.getResourcesByType(type as ResourceType))
        .map(resource => ({
          id: resource.id,
          name: resource.data['TemplateDetailsData']?.templateName ?? resource.metaData.name,
          type: resource.metaData.type
        }));

    });
    // Initialize calendar state for the first tab
    this.initializeTabCalendarState(0);
    this.setupMouseEvents();

    this.requisitionForm.get('isOpen')?.valueChanges.subscribe(value => {
      this.logger.i('Shutter state changed:', value);
    });

    if (!this.pluginData.TrainingSchedulesData[0]) {
      this.pluginData.TrainingSchedulesData[0] = {} as any;
    }
    // if (!this.pluginData.TrainingSchedulesData[0].removalOption) {
    //   this.pluginData.TrainingSchedulesData[0].removalOption = 'immediate';
    // }

    this.userGetByRole('trainer');
  }


  userGetByRole(role: string): void {

    this.loginEntityService.userGetByRole(role).subscribe({
      next: (result) => {
        this.trainers = result;
        this.logger.i('Loaded Trainer:', this.trainers);
      },
      error: (err) => {
        this.logger.e('Error loading users:', err);
      }
    });
  }


  onSliderChange(event: any): void {
    const isOpen = event.target.checked;
    this.logger.i('Slider changed to:', isOpen);

    // You can add additional logic here
    if (isOpen) {
      this.logger.i('Opening shutter...');
    } else {
      this.logger.i('Closing shutter...');
    }
  }

  // Method to get current slider value
  getSliderValue(): boolean {
    return this.requisitionForm.get('isOpen')?.value || false;
  }

  // Method to programmatically set slider value
  setSliderValue(value: boolean): void {
    this.requisitionForm.patchValue({ isOpen: value });
  }

  // loadTrainers(): void {
  //   this.trainers = this.trainerService.getAllTrainers();
  // }

  // validate(): boolean {
  //   return !!this.pluginData.displayName;//.customerName;
  // }

    validate(): boolean {
    return this.validateDateRanges();// // !!this.pluginData.displayName;
  }

private validateDateRanges(): boolean {
  for (let i = 0; i < this.schedules.length; i++) {
    const drArray = this.getDateRangesArray(i);
    if (!drArray || drArray.length === 0) {
      this.snackBar.open(`Batch ${i + 1}: at least one date range is required.`, 'Dismiss', { duration: 3000 });
      return false;
    }
    for (let j = 0; j < drArray.length; j++) {
      const ctrl = drArray.at(j) as any;
      if (!ctrl.get('startDate')?.value || !ctrl.get('endDate')?.value) {
        this.snackBar.open(`Batch ${i + 1}, Date Range ${j + 1}: start and end date are required.`, 'Dismiss', { duration: 3000 });
        drArray.at(j).markAllAsTouched();
        return false;
      }
      if (!ctrl.get('startTime')?.value || !ctrl.get('endTime')?.value) {
        this.snackBar.open(`Batch ${i + 1}, Date Range ${j + 1}: start and end time are required.`, 'Dismiss', { duration: 3000 });
        drArray.at(j).markAllAsTouched();
        return false;
      }
    }
  }
  return true;
}


  isComplete(): boolean {
    return this.validate();
  }
  reset(): void {
    // this.pluginData = new CustomerAllocationData();
    this.pluginData = new TrainingSchedulesData();
  }
  asPreview?(): Type<IPluginPreviewComponent> {
    // Always return the preview component for this plugin
    return TrainingSchedulesPreviewComponent;
  }



  // Helper methods to get control values
  getVMControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('vmControls') as FormGroup;
  }

  getCloudDBControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('cloudDbControls') as FormGroup;
  }

  getAppServiceControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('appServiceControls') as FormGroup;
  }

  getUserAccountControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('userAccountControls') as FormGroup;
  }


  createNewVMTemplate() {

    this.router.navigate(['/wizard', 'Virtual Machine']);

  }

  setRemovalOption1(option: 'immediate' | 'scheduled' | 'manual'): void {

    this.getPluginData();

    if (!this.pluginData.TrainingSchedulesData[this.activeTabIndex]) {
      this.pluginData.TrainingSchedulesData[this.activeTabIndex] = {} as any;
    }
    this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalOption = option;
    if (option === 'manual') {
      // Clear removal date time when manual is selected
      this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime = new Date();
    } else if (option === 'scheduled' && !this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime) {
      // Default to 1 day after the allocation end date, or tomorrow if no end date
      const base = this.pluginData.TrainingSchedulesData[this.activeTabIndex]?.endDate || new Date();
      const defaultRemovalDate = new Date(base);
      defaultRemovalDate.setDate(defaultRemovalDate.getDate() + 1);
      this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime = defaultRemovalDate;
    }

    // // Re-validate
    // // this.validateRemovalDate();
  }

  // Helper method to calculate deletion time (end date + active end time + 1 hour)
  getDeletionTime(): string {
    if (!this.pluginData.TrainingSchedulesData[this.activeTabIndex]?.endDate) {
      return "1 hour after allocation ends";
    }

    // Create a new date from the end date
    const deletionDate = new Date(this.pluginData.TrainingSchedulesData[this.activeTabIndex]?.endDate || new Date());

    // Set the hours to activeEnd + 1
    // deletionDate.setHours(this.pluginData.activeEnd + 1, 0, 0, 0);

    // Format the date
    return this.formatDateTimeForDisplay(deletionDate);
  }

  // Helper to format date and time for display
  formatDateTimeForDisplay(date: Date): string {
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();

    // Format time (12-hour format with AM/PM)
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
  }

  // Helper method to get minimum date for removal date (day after end date)
  get minRemovalDate(): Date {
    if (!this.pluginData.TrainingSchedulesData[this.activeTabIndex]?.endDate) {
      return new Date();
    }

    // Always return a date that's one day after the end date
    const minRemoval = new Date(this.pluginData.TrainingSchedulesData[this.activeTabIndex]?.endDate || new Date());
    minRemoval.setDate(minRemoval.getDate() + 1);
    return minRemoval;
  }


  validateRemovalDate(): void {
    if (this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalOption !== 'scheduled') {
      this.removalDateValid = true;
      return;
    }

    if (!this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime) {
      this.removalDateValid = false;
      return;
    }

    if (!this.pluginData.TrainingSchedulesData[this.activeTabIndex].endDate) {
      this.removalDateValid = true;
      return;
    }

    // Removal date must be after end date
    this.removalDateValid = new Date(this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime ?? new Date(0)) > new Date(this.pluginData.TrainingSchedulesData[this.activeTabIndex].endDate ?? new Date(0));
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private createForm() {

    this.requisitionForm = this.fb.group({
      trainingSchedules: this.fb.array([this.createScheduleGroup()]),
      accessViaInvitedEmailOnly: [true] // Default to closed

      , courseId: '-1',
    });
    this.timeSelectionForm = this.fb.group({
      startTime: [this.tempStartTime, Validators.required],
      endTime: [this.tempEndTime, Validators.required],
      trainingTitle: [this.tempTitle, Validators.required]
    });


  }


  private createScheduleGroup(): FormGroup {
    return this.fb.group({
      title: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      offDates: [''],
      participants: [''],
      trainers: [''],
      resourceType: [''], // This field stores the selected resource type
      resourceTemplate: [''],
      courseId: ['-1', Validators.required],
      deletionType: [''],//['immediate'], // Default to immediate
      deletionDate: [''],
      deletionTime: [''],
      // Dynamic resource controls will be added here
      vmControls: this.fb.group({}), // For VM specific controls
      cloudDbControls: this.fb.group({}), // For CloudDB specific controls
      appServiceControls: this.fb.group({}), // For AppService specific controls
      userAccountControls: this.fb.group({}), // For UserAccount specific controls
      _resourceValues: this.fb.control({}), // Hidden control to store resource values
      trainersRemarks: [''],
      trainingDetail: [''],
      resourceTemplateIds: [''], // Array to hold resource template IDs
      vmTemplate: [''], // Specific field for VM template
      accessViaInvitedEmailOnly: true,
      dateRanges: this.fb.array([this.createDateRangeGroup()]), // Date ranges for this batch
    });

  }

  get schedules(): FormArray {
    return this.requisitionForm.get('trainingSchedules') as FormArray;
  }

  getSelectedResourceType(scheduleIndex: number): string {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('resourceType')?.value || '';
  }

  getResourceTemplatesForType(resourceType: string): ResourceLite[] {
    return this.resourceTemplatesByType[resourceType as keyof typeof this.resourceTemplatesByType] || [];
  }

  addNewTemplate(scheduleIndex: number): void {
    const selectedType = this.getSelectedResourceType(scheduleIndex);
    if (selectedType) {
      // Logic to open template creation form/dialog would go here
      alert(`Launch ${selectedType} template creation form`);
    }
  }

  removeSchedule(index: number): void {
    if (this.schedules.length > 1) {
      this.schedules.removeAt(index);

      // Adjust active tab if necessary
      if (this.activeTabIndex >= this.schedules.length) {
        this.activeTabIndex = this.schedules.length - 1;
      } else if (this.activeTabIndex > index) {
        this.activeTabIndex--;
      }
    }
  }

  toggleSection(sectionElement: HTMLElement): void {
    const title = sectionElement.querySelector('.section-title');
    const content = sectionElement.querySelector('.section-content');

    if (title && content) {
      title.classList.toggle('expanded');
      content.classList.toggle('expanded');
    }
  }

  private initializeSectionsAsCollapsed(): void {
    setTimeout(() => {
      const allSections = document.querySelectorAll('.form-section');
      allSections.forEach(section => {
        const title = section.querySelector('.section-title');
        const content = section.querySelector('.section-content');

        if (title && content) {
          title.classList.remove('expanded');
          content.classList.remove('expanded');
        }
      });
    }, 0);
  }

  addParticipant(scheduleIndex: number, input: HTMLInputElement): void {
    const value = input.value.trim();
    if (!value) return;

    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentParticipants = schedule.get('participants')?.value || '';

    // Check if participant already exists
    const existingParticipants = currentParticipants.split(',').map((p: string) => p.trim());
    if (existingParticipants.includes(value)) {
      input.value = '';
      return; // Don't add duplicate
    }

    const newParticipants = currentParticipants ?
      `${currentParticipants},${value}` : value;

    schedule.patchValue({ participants: newParticipants });
    input.value = '';
  }

  addTrainer(scheduleIndex: number, input: HTMLInputElement): void {
    const value = input.value.trim();
    if (!value) return;

    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentTrainers = schedule.get('trainers')?.value || '';

    // Check if trainer already exists
    const existingTrainers = currentTrainers.split(',').map((t: string) => t.trim());
    if (existingTrainers.includes(value)) {
      input.value = '';
      return; // Don't add duplicate
    }

    const newTrainers = currentTrainers ?
      `${currentTrainers},${value}` : value;

    schedule.patchValue({ trainers: newTrainers });
    input.value = '';
  }

  removeParticipantChip(scheduleIndex: number, participant: string): void {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentParticipants = (schedule.get('participants')?.value || '').split(',');
    const updatedParticipants = currentParticipants
      .filter((p: string) => p.trim() !== participant.trim())
      .join(',');

    schedule.patchValue({ participants: updatedParticipants });
  }

  removeTrainerChip(scheduleIndex: number, trainer: string): void {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentTrainers = (schedule.get('trainers')?.value || '').split(',');
    const updatedTrainers = currentTrainers
      .filter((t: string) => t.trim() !== trainer.trim())
      .join(',');

    schedule.patchValue({ trainers: updatedTrainers });
  }

  createNewResource(): void {
    // Logic to open resource creation form/dialog would go here
    alert('Launch resource creation form');
  }

  saveAsDraft(): void {
    // Logic to save form as draft would go here
    this.logger.i('Saving as draft:', this.requisitionForm.value);
  }


  // Add this mapping configuration at the top of your component class
  private resourceTypeConfig = {
    vm: { controlGroup: 'vmControls', templateField: 'vmTemplate' },
    db: { controlGroup: 'cloudDbControls', templateField: 'dbType' },
    app: { controlGroup: 'appServiceControls', templateField: 'servicePlan' },
    acc: { controlGroup: 'userAccountControls', templateField: 'accountType' }
  };

  private getResourceTemplateId(scheduleData: any): string | null {
    const resourceType = scheduleData.resourceType;

    // Return null if no resource type is selected
    const config = this.resourceTypeConfig[resourceType as keyof typeof this.resourceTypeConfig];

    // Use optional chaining and nullish coalescing for clean extraction
    return config ? (scheduleData[config.controlGroup]?.[config.templateField] ?? null) : null;
  }

  async submitRequisition(): Promise<void> {
    if (this.requisitionForm.invalid) {
      this.markFormGroupAsTouched(this.requisitionForm);
      this.snackBar.open('Please fix validation errors before submitting.', 'Dismiss', { duration: 3000 });
      return;
    }

    const formValue = this.requisitionForm.value;

    const newReq = new Requisition();


    // Training Schedules
    newReq.trainingSchedules = formValue.trainingSchedules.map((sched: any, index: number) => {
      const ts = new TrainingSchedule();
      ts.srNo = index + 1;
      ts.title = sched.title;
      ts.startDate = new Date(sched.startDate);
      ts.endDate = new Date(sched.endDate);
      ts.startTime = sched.startTime;
      ts.endTime = sched.endTime;

      // offDates string → Date[]
      ts.offDates = (sched.offDates || '')
        .split(',')
        .map((d: string) => d.trim())
        .filter((d: any) => d)
        .map((d: string) => new Date(d));

      // participants from comma-separated string
      ts.participants = (sched.participants || '')
        .split(',')
        .map((e: string) => e.trim())
        .filter((e: any) => e);
      // trainers (if needed in model, currently left empty)
      const trainer = this.trainerService.getTrainerById(sched.trainers);
      ts.trainers = [];
      if (trainer)
        ts.trainers.push({ id: trainer.id, name: trainer.name });

      // ts.course = this.courseService.getCourseById(sched.courseId);

      ts.course = this.courseService.getCourseById(this.defaultCustomCourseId);


      const resourceTemplateId = this.getResourceTemplateId(sched);
      ts.resourceTemplateIds = resourceTemplateId ? [resourceTemplateId] : [];


      // Deletion Policy
      ts.resourcesManagementPolicies.deletion.deletionType = sched.deletionType;
      if (sched.deletionDate) {
        ts.resourcesManagementPolicies.deletion.deletionDate = new Date(sched.deletionDate);
      }
      ts.resourcesManagementPolicies.deletion.deletionTime = sched.deletionTime || '';

      // ts.trainersRemarks = sched.trainersRemarks || '';
      // ts.trainingDetail = sched.trainingDetail || '';
      this.logger.i('Training Schedule:', ts);

      return ts;
    });

    // Save via service
    const createdReq = await this.reqService.createRequisition(newReq, 'admin');

    this.logger.i('Created Requisition:', createdReq);
    // Create classes
    this.createClassForRequisition(createdReq);

    // Notify success
    this.snackBar.open('Requisition submitted successfully!', 'Dismiss', { duration: 3000 });

    // Optionally reset form or navigate away
    // this.requisitionForm.reset();
    // this.router.navigate(['/requisitions']);
  }

  createClassForTrainingSchedules(schedules: TrainingSchedule[], customerId: string): ClassesEntity[] {
    let classes: ClassesEntity[] = [];
    schedules.forEach(sch => this.classService.createClass(sch, customerId));
    return classes;
  }

  createClassForRequisition(requisition: Requisition): ClassesEntity[] {
    return this.createClassForTrainingSchedules(requisition.trainingSchedules, requisition.customerDetails.id);
  }


  cancelRequisition(): void {
    // Logic to cancel or reset form would go here
    if (confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      this.createForm();
      this.ngOnInit();
    }
  }

  private markFormGroupAsTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupAsTouched(control);
      }
      else if (control instanceof FormArray) {
        control.controls.forEach(item => {
          if (item instanceof FormGroup) {
            this.markFormGroupAsTouched(item);
          }
        });
      }
    });
  }

  getParticipantsArray(scheduleIndex: number): string[] {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const participantsStr = schedule.get('participants')?.value || '';
    return participantsStr.split(',').filter((p: string) => p.trim() !== '');
  }

  getTrainersArray(scheduleIndex: number): string[] {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const trainersStr = schedule.get('trainers')?.value || '';
    return trainersStr.split(',').filter((t: string) => t.trim() !== '');
  }

  // Resource type management with dynamic controls
  selectResourceType(scheduleIndex: number, resourceType: string): void {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentResourceType = schedule.get('resourceType')?.value;
    this.logger.i(currentResourceType);
    this.logger.i(resourceType);

    // here i have to update resourceTemplates
    this.resourceTemplates = Array.isArray(this.resourceTemplatesByType[resourceType]) ? this.resourceTemplatesByType[resourceType] : [];


    // Only clear and recreate controls if the resource type actually changed
    if (currentResourceType !== resourceType) {
      // Store current values before clearing
      const currentValues = this.getCurrentResourceValues(schedule, currentResourceType);

      schedule.patchValue({
        resourceType: resourceType,
        resourceTemplate: '' // Reset template when type changes
      });

      // Clear all resource-specific controls
      this.clearResourceControls(schedule);

      // Add controls based on selected type
      this.addResourceTypeControls(schedule, resourceType);

      // If switching back to a previously used type, restore its values
      if (currentValues[resourceType]) {
        this.restoreResourceValues(schedule, resourceType, currentValues[resourceType]);
      }
    }
  }

  private getCurrentResourceValues(schedule: FormGroup, resourceType: string): any {
    const storedValues = schedule.get('_resourceValues')?.value || {};

    if (resourceType) {
      const controlGroupName = this.getControlGroupName(resourceType);
      const controlGroup = schedule.get(controlGroupName) as FormGroup;
      if (controlGroup) {
        storedValues[resourceType] = controlGroup.value;
      }
    }

    // Store back to form (create the control if it doesn't exist)
    if (!schedule.get('_resourceValues')) {
      // schedule.addControl('_resourceValues', this.fb.control(storedValues));
    } else {
      schedule.get('_resourceValues')?.setValue(storedValues);
    }

    return storedValues;
  }

  // Helper method to restore resource values
  private restoreResourceValues(schedule: FormGroup, resourceType: string, values: any): void {
    const controlGroupName = this.getControlGroupName(resourceType);
    const controlGroup = schedule.get(controlGroupName) as FormGroup;
    if (controlGroup && values) {
      controlGroup.patchValue(values);
    }
  }

  // Helper method to get control group name based on resource type
  private getControlGroupName(resourceType: string): string {
    switch (resourceType) {
      case 'vm': return 'vmControls';
      case 'db': return 'cloudDbControls';
      case 'app': return 'appServiceControls';
      case 'acc': return 'userAccountControls';
      default: return '';
    }
  }

  private clearResourceControls(schedule: FormGroup): void {
    const resourceControlGroups = ['vmControls', 'cloudDbControls', 'appServiceControls', 'userAccountControls'];
    resourceControlGroups.forEach(groupName => {
      const controlGroup = schedule.get(groupName) as FormGroup;
      Object.keys(controlGroup.controls).forEach(key => {
        controlGroup.removeControl(key);
      });
    });
  }

  private addResourceTypeControls(schedule: FormGroup, resourceType: string): void {
    switch (resourceType) {
      case 'vm':
        this.addVMControls(schedule);
        break;
      case 'db':
        this.addCloudDBControls(schedule);
        break;
      case 'app':
        this.addAppServiceControls(schedule);
        break;
      case 'acc':
        this.addUserAccountControls(schedule);
        break;
    }
  }

  private addVMControls(schedule: FormGroup): void {
    const vmGroup = schedule.get('vmControls') as FormGroup;
    vmGroup.addControl('vmTemplate', this.fb.control('Angular Developer VM'));
    vmGroup.addControl('operatingSystem', this.fb.control('Windows Server 2019'));
    vmGroup.addControl('diskSize', this.fb.control('128'));
    vmGroup.addControl('networkConfig', this.fb.control('Default'));
    vmGroup.addControl('additionalSoftware', this.fb.control(''));
  }

  private addCloudDBControls(schedule: FormGroup): void {
    const cloudDbGroup = schedule.get('cloudDbControls') as FormGroup;
    cloudDbGroup.addControl('dbType', this.fb.control('MySQL Template'));
    cloudDbGroup.addControl('dbVersion', this.fb.control('8.0'));
    cloudDbGroup.addControl('storageSize', this.fb.control('20'));
    cloudDbGroup.addControl('performanceTier', this.fb.control('Basic'));
    cloudDbGroup.addControl('backupRetention', this.fb.control('7'));
  }

  private addAppServiceControls(schedule: FormGroup): void {
    const appServiceGroup = schedule.get('appServiceControls') as FormGroup;
    appServiceGroup.addControl('servicePlan', this.fb.control('Web App Template'));
    appServiceGroup.addControl('runtime', this.fb.control('.NET 6'));
    appServiceGroup.addControl('customDomain', this.fb.control(''));
    appServiceGroup.addControl('sslEnabled', this.fb.control(true));
    appServiceGroup.addControl('environmentVariables', this.fb.control(''));
  }

  private addUserAccountControls(schedule: FormGroup): void {
    const userAccountGroup = schedule.get('userAccountControls') as FormGroup;
    userAccountGroup.addControl('accountType', this.fb.control('Admin Account'));
    userAccountGroup.addControl('permissions', this.fb.control('Read-Write'));
    userAccountGroup.addControl('expirationDate', this.fb.control(''));
    userAccountGroup.addControl('defaultPassword', this.fb.control(''));
    userAccountGroup.addControl('requirePasswordChange', this.fb.control(true));
  }

  formatDateCalendar(date: Date): string {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  //end


  getMinMaxDates(dateRanges: any[]) {
    if (!dateRanges || dateRanges.length === 0) {
      return { minStartDate: null, maxEndDate: null };
    }

    const startDates = dateRanges.map(range => new Date(range.startDate).getTime());
    const endDates = dateRanges.map(range => new Date(range.endDate).getTime());

    const minStartDate = new Date(Math.min(...startDates));
    const maxEndDate = new Date(Math.max(...endDates));

    return { minStartDate, maxEndDate };
  }

  // modified code for calendar

  // Initialize calendar state for a specific tab
  private initializeTabCalendarState(tabIndex: number): void {
    if (!this.tabCalendarStates[tabIndex]) {
      this.tabCalendarStates[tabIndex] = {
        currentDate: new Date(),
        calendarDates: [],
        dateRanges: [],
        isPanelOpen: false,
        isSelecting: false,
        selectionStart: null,
        selectionEnd: null,
        currentHoverDate: null
      };
      this.generateCalendarDatesForTab(tabIndex);
    }
  }

  // Generate calendar dates for a specific tab
  generateCalendarDatesForTab(tabIndex: number): void {
    const state = this.tabCalendarStates[tabIndex];
    if (!state) return;

    const year = state.currentDate.getFullYear();
    const month = state.currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    state.calendarDates = [];
    const currentCalendarDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      state.calendarDates.push({
        date: new Date(currentCalendarDate),
        isCurrentMonth: currentCalendarDate.getMonth() === month
      });
      currentCalendarDate.setDate(currentCalendarDate.getDate() + 1);
    }
  }

  // Tab management - ensure calendar state exists when switching tabs
  setActiveTab(index: number): void {
    this.activeTabIndex = index;
    this.initializeTabCalendarState(index);
  }

  // Add schedule with calendar state initialization
  addSchedule(): void {

    this.logger.i('before add schedule', this.pluginData);
    const newIndex = this.schedules.length;
    const scheduleGroup = this.createScheduleGroup();

    scheduleGroup.patchValue({
      title: `Batch ${newIndex + 1}`
    });

    this.schedules.push(scheduleGroup);
    this.activeTabIndex = newIndex;

    if (!this.pluginData.TrainingSchedulesData[newIndex]) {
      this.pluginData.TrainingSchedulesData[newIndex] = {} as any;
    }

    this.pluginData.TrainingSchedulesData[newIndex].removalOption = 'immediate';

    // Initialize calendar state for the new tab
    this.initializeTabCalendarState(newIndex);

    //  this.pluginData.TrainingSchedulesData[newIndex].removalOption='immediate';

    this.logger.i('add schedule', this.pluginData);

  }

  // Remove schedule and clean up calendar state
  removeCurrentSchedule(): void {
    if (this.schedules.length > 1) {
      const removedIndex = this.activeTabIndex;
      this.schedules.removeAt(this.activeTabIndex);

      // Clean up calendar state for removed tab
      delete this.tabCalendarStates[removedIndex];

      // Adjust active tab if necessary
      if (this.activeTabIndex >= this.schedules.length) {
        this.activeTabIndex = this.schedules.length - 1;
      }

      // Reinitialize calendar state for current tab
      this.initializeTabCalendarState(this.activeTabIndex);
    } else {
      alert('At least one training schedule is required.');
    }
  }

  // Calendar navigation methods - updated to work with active tab
  getMonthYear(): string {
    const state = this.tabCalendarStates[this.activeTabIndex];
    return state ? state.currentDate.toLocaleString('default', { month: 'long', year: 'numeric' }) : '';
  }

  previousMonth(): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state) {
      state.currentDate.setMonth(state.currentDate.getMonth() - 1);
      this.generateCalendarDatesForTab(this.activeTabIndex);
    }
  }

  nextMonth(): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state) {
      state.currentDate.setMonth(state.currentDate.getMonth() + 1);
      this.generateCalendarDatesForTab(this.activeTabIndex);
    }
  }

  // Date selection methods - updated to work with active tab
  startDateSelection(event: MouseEvent, date: Date): void {
    event.preventDefault();
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state) {
      state.isSelecting = true;
      state.selectionStart = new Date(date);
      state.selectionEnd = new Date(date);
      state.currentHoverDate = new Date(date);
    }
  }

  onDateHover1(date: Date): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && state.isSelecting && state.selectionStart) {
      state.currentHoverDate = new Date(date);
      state.selectionEnd = new Date(date);
    }
  }


  private monthChangeTimeout: any = null;
  private monthChangeDirection: 'next' | 'prev' | null = null;

  onDateHover12(date: Date): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && state.isSelecting && state.selectionStart) {
      const calendarDates = state.calendarDates;
      const index = calendarDates.findIndex(d => this.isSameDate(d.date, date));
      const totalRows = 6; // we always render 42 days in 6 rows
      const rowIndex = Math.floor(index / 7);

      if (rowIndex <= 0) {
        // Top row → move previous month
        this.scheduleMonthChange('prev');
      } else if (rowIndex >= totalRows - 1) {
        // Bottom row → move next month
        this.scheduleMonthChange('next');
      } else {
        // Middle area → cancel month change
        this.cancelMonthChange();
      }

      state.currentHoverDate = new Date(date);
      state.selectionEnd = new Date(date);
    }
  }

  onDateHover(date: Date): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && state.isSelecting && state.selectionStart) {
      const calendarDates = state.calendarDates;
      const index = calendarDates.findIndex(d => this.isSameDate(d.date, date));

      const totalRows = 6; // 42 days
      const rowIndex = Math.floor(index / 7);
      const colIndex = index % 7; // 0 = Sunday, 6 = Saturday

      const canGoPrev = this.canNavigateMonth('prev');
      const canGoNext = this.canNavigateMonth('next');

      // Horizontal + Vertical detection with date guard
      if ((rowIndex <= 0 || colIndex === 0) && canGoPrev) {
        this.scheduleMonthChange('prev');
      }
      else if ((rowIndex >= totalRows - 1 || colIndex === 6) && canGoNext) {
        this.scheduleMonthChange('next');
      }
      else {
        this.cancelMonthChange();
      }

      state.currentHoverDate = new Date(date);
      state.selectionEnd = new Date(date);
    }
  }

  private canNavigateMonth(direction: 'prev' | 'next'): boolean {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (!state) return false;

    const newDate = new Date(state.currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    // Check if the target month has at least one selectable date
    const year = newDate.getFullYear();
    const month = newDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const testDate = new Date(year, month, day);
      if (!this.isPastDate(testDate)) {
        return true; // found a selectable date
      }
    }
    return false; // all past dates, don't navigate
  }


  private scheduleMonthChange(direction: 'next' | 'prev') {
    if (this.monthChangeDirection !== direction) {
      this.cancelMonthChange(); // Reset any previous timer
      this.monthChangeDirection = direction;
      this.monthChangeTimeout = setTimeout(() => {
        if (direction === 'next') this.nextMonth();
        else this.previousMonth();
        this.monthChangeDirection = null;
      }, 400); // delay 0.4 sec before changing month
    }
  }

  private cancelMonthChange() {
    if (this.monthChangeTimeout) {
      clearTimeout(this.monthChangeTimeout);
      this.monthChangeTimeout = null;
    }
    this.monthChangeDirection = null;
  }



  endDateSelection(date: Date): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && state.isSelecting && state.selectionStart) {
      state.selectionEnd = new Date(date);

      const startDate = state.selectionStart <= state.selectionEnd ? state.selectionStart : state.selectionEnd;
      const endDate = state.selectionStart <= state.selectionEnd ? state.selectionEnd : state.selectionStart;

      this.tempDateRange = { start: startDate, end: endDate };
      this.showTimeModal = true;
      this.tempTitle = '';
      this.editingRange = null;
      this.editingTabIndex = this.activeTabIndex;
    }
    if (state) {
      state.isSelecting = false;
    }
  }

  // Date checking methods - updated to work with active tab
  isDateSelected(date: Date): boolean {
    const state = this.tabCalendarStates[this.activeTabIndex];
    return state ? state.dateRanges.some(range =>
      date >= range.startDate && date <= range.endDate
    ) : false;
  }

  isDateInRange(date: Date): boolean {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (!state || !state.isSelecting || !state.selectionStart || !state.currentHoverDate) {
      return false;
    }

    const start = state.selectionStart <= state.currentHoverDate ? state.selectionStart : state.currentHoverDate;
    const end = state.selectionStart <= state.currentHoverDate ? state.currentHoverDate : state.selectionStart;

    return date > start && date < end;
  }

  isRangeStart(date: Date): boolean {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && state.isSelecting && state.selectionStart && state.currentHoverDate) {
      const start = state.selectionStart <= state.currentHoverDate ? state.selectionStart : state.currentHoverDate;
      return this.isSameDate(date, start);
    }
    return state ? state.dateRanges.some(range => this.isSameDate(date, range.startDate)) : false;
  }

  isRangeEnd(date: Date): boolean {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && state.isSelecting && state.selectionStart && state.currentHoverDate) {
      const end = state.selectionStart <= state.currentHoverDate ? state.currentHoverDate : state.selectionStart;
      return this.isSameDate(date, end);
    }
    return state ? state.dateRanges.some(range => this.isSameDate(date, range.endDate)) : false;
  }

  // Time modal methods - updated to work with specific tab
  saveTimeSelection(): void {
    this.logger.i('form values', this.timeSelectionForm.value);
    this.tempStartTime = this.timeSelectionForm.get('startTime')?.value;
    this.tempEndTime = this.timeSelectionForm.get('endTime')?.value;
    this.tempTitle = this.timeSelectionForm.get('trainingTitle')?.value;
    if (this.tempDateRange && this.tempStartTime && this.tempEndTime) {
      if (this.tempStartTime >= this.tempEndTime) {
        alert('End time must be after start time');
        return;
      }

      const state = this.tabCalendarStates[this.editingTabIndex];
      if (!state) return;

      if (this.editingRange) {
        // Update existing range
        this.editingRange.startDate = this.tempDateRange.start;
        this.editingRange.endDate = this.tempDateRange.end;
        this.editingRange.startTime = this.tempStartTime;
        this.editingRange.endTime = this.tempEndTime;
        this.editingRange.title = this.tempTitle || this.editingRange.title;
      } else {
        // Create new range
        const newRange: DateRange = {
          id: this.generateId(),
          startDate: this.tempDateRange.start,
          endDate: this.tempDateRange.end,
          startTime: this.tempStartTime,
          endTime: this.tempEndTime,
          title: this.tempTitle
        };
        state.dateRanges.push(newRange);
      }

      this.closeTimeModal();
      this.logger.i('Date range saved for tab', this.editingTabIndex, ':', state.dateRanges);
    }
  }

  closeTimeModal(): void {
    this.showTimeModal = false;
    this.tempDateRange = null;
    this.editingRange = null;
    const state = this.tabCalendarStates[this.editingTabIndex];
    if (state) {
      state.selectionStart = null;
      state.selectionEnd = null;
      state.currentHoverDate = null;
    }
  }

  editRange(range: DateRange): void {
    this.editingRange = range;
    this.editingTabIndex = this.activeTabIndex;
    this.tempDateRange = { start: range.startDate, end: range.endDate };
    this.tempStartTime = range.startTime;
    this.tempEndTime = range.endTime;
    this.tempTitle = range.title || '';
    this.showTimeModal = true;
  }

  removeRange(id: string): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state) {
      state.dateRanges = state.dateRanges.filter(range => range.id !== id);
    }
  }

  togglePanel(): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state) {
      state.isPanelOpen = !state.isPanelOpen;
    }
  }

  clearAllSchedules(): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (state && confirm('Are you sure you want to clear all training schedules for this tab?')) {
      state.dateRanges = [];
    }
  }

  exportSchedules(): void {
    const state = this.tabCalendarStates[this.activeTabIndex];
    if (!state) return;

    const scheduleData = state.dateRanges.map((range, index) => ({
      title: range.title || `Batch ${index + 1}`,
      startDate: this.formatDate(range.startDate),
      endDate: this.formatDate(range.endDate),
      startTime: range.startTime,
      endTime: range.endTime
    }));

    const dataStr = JSON.stringify(scheduleData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `training-schedule-tab-${this.activeTabIndex + 1}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  setupMouseEvents(): void {
    document.addEventListener('mouseup', () => {
      // Clear selection state for all tabs
      Object.values(this.tabCalendarStates).forEach(state => {
        if (state.isSelecting) {
          state.isSelecting = false;
        }
      });
    });
  }

  // Helper methods to get current tab's state
  getCurrentTabDateRanges(): DateRange[] {
    const state = this.tabCalendarStates[this.activeTabIndex];
    return state ? state.dateRanges : [];
  }

  getCurrentTabCalendarDates(): { date: Date, isCurrentMonth: boolean }[] {
    const state = this.tabCalendarStates[this.activeTabIndex];
    return state ? state.calendarDates : [];
  }

  getCurrentTabPanelState(): boolean {
    const state = this.tabCalendarStates[this.activeTabIndex];
    return state ? state.isPanelOpen : true;
  }
  //end


  // Main Save Method - This captures all training schedules data
  saveAllTrainingSchedules() {
    if (this.requisitionForm.valid) {
      const allSchedulesData = this.getAllTrainingSchedulesData();

      // Here you can call your API or service to save the data
      this.logger.i('All Training Schedules Data:', allSchedulesData);

      // Example API call
      // this.trainingService.saveTrainingSchedules(allSchedulesData).subscribe(
      //   response => {
      //     this.logger.i('Schedules saved successfully:', response);
      //     // Handle success
      //   },
      //   error => {
      //     this.logger.e('Error saving schedules:', error);
      //     // Handle error
      //   }
      // );

      alert('Training Schedules saved successfully!');
    } else {
      this.markFormGroupTouched(this.requisitionForm);
      alert('Please fill in all required fields.');
    }
  }

  // Get all training schedules data in structured format
  getAllTrainingSchedulesData(): TrainingScheduleData[] {
    const schedulesData: TrainingSchedulesData = new TrainingSchedulesData();

    this.schedules.controls.forEach((scheduleControl, index) => {
      const schedule = scheduleControl as FormGroup;

      // Ensure form arrays are updated with latest data
      this.updateParticipantsFormArray(index);
      this.updateDateRangesFormArray(index);

      const scheduleData: TrainingScheduleData = {
        title: schedule.get('title')?.value || '',
        course: schedule.get('courseId')?.value || '',
        participants: this.tabParticipants[index] || [],
        trainers: schedule.get('trainers')?.value || [],
        dateRanges: this.tabDateRanges[index] || [],
        // resourceType: schedule.get('resourceType')?.value || '',
        removalOption: schedule.get('removalOption')?.value || 'immediate',
        removalDateTime: schedule.get('removalDateTime')?.value || null,
        // activeEnd: 0,
        // activeStart: 0,
        trainersRemarks: schedule.get('trainersRemarks')?.value || '',
        trainingDetail: schedule.get('trainingDetail')?.value || '',
        resourceTemplateIds: schedule.get('vmTemplate')?.value || [],
        accessViaInvitedEmailOnly: true, // locked — customisation disabled
      };

      // Add resource-specific data based on resource type
      // const resourceType = scheduleData.resourceType;
      // if (resourceType === 'vm') {
      //   scheduleData.vmControls = schedule.get('vmControls')?.value;
      // } else if (resourceType === 'db') {
      //   scheduleData.cloudDBControls = schedule.get('cloudDBControls')?.value;
      // } else if (resourceType === 'app') {
      //   scheduleData.appServiceControls = schedule.get('appServiceControls')?.value;
      // } else if (resourceType === 'acc') {
      //   scheduleData.userAccountControls = schedule.get('userAccountControls')?.value;
      // }

      schedulesData.TrainingSchedulesData.push(scheduleData);
    });

    return schedulesData.TrainingSchedulesData;
  }

  // Utility method to mark all form controls as touched for validation
  private markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }


  private updateParticipantsFormArray(scheduleIndex: number) {
    const participantsArray = this.getParticipantsFormArray(scheduleIndex);
    participantsArray.clear();

    this.tabParticipants[scheduleIndex].forEach(participant => {
      participantsArray.push(this.fb.control(participant));
    });
  }

  getParticipantsFormArray(scheduleIndex: number): FormArray {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('participants') as FormArray;
  }

  private updateDateRangesFormArray(scheduleIndex: number) {
    const dateRangesArray = this.getDateRangesFormArray(scheduleIndex);
    dateRangesArray.clear();

    this.tabDateRanges[scheduleIndex].forEach(dateRange => {
      dateRangesArray.push(this.createDateRangeFormGroup(dateRange));
    });
  }
  getDateRangesFormArray(scheduleIndex: number): FormArray {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('dateRanges') as FormArray;
  }


  private createDateRangeFormGroup(dateRange: DateRange): FormGroup {
    return this.fb.group({
      id: [dateRange.id],
      title: [dateRange.title],
      startDate: [dateRange.startDate],
      endDate: [dateRange.endDate],
      startTime: [dateRange.startTime],
      endTime: [dateRange.endTime]
    });
  }

  selectResourceTemplate(scheduleIndex: number, templateId: string): void {
    // const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    // const resourceType = schedule.get('resourceType')?.value;

    // const selectedResourceTemplate = this.resourceTemplatesByType[resourceType].find(t => t.id === templateId);
    // // const prepared = { id: templateId, name: selectedResourceTemplate!.name, type: resourceType };
    // const prepared = { id: templateId, name: selectedResourceTemplate!.name, resourceType: resourceType };
    // //before pushing, check if it already exists
    // this.selectedTemplateIds.push(prepared);

  }

  customCourse: CourseEntity = new CourseEntity();

  getPluginData(): BasePluginData {
    this.customCourse.code = 'Custom';
    this.customCourse.name = 'Custom Course';
    this.customCourse.id = this.defaultCustomCourseId;


    this.pluginData.TrainingSchedulesData = []; // Clear old data

    this.schedules.controls.forEach((scheduleControl, index) => {
      const schedule = scheduleControl as FormGroup;

      const ts = new TrainingScheduleData();
      ts.title = schedule.get('title')?.value;
      ts.course = this.customCourse;//this.courseService.getCourseById(this.defaultCustomCourseId)||new CourseEntity();//schedule.get('courseId')?.value) || new CourseEntity();
      ts.participants = (schedule.get('participants')?.value || '').split(',').filter((p: string) => p.trim());
      ts.trainers = this.trainers.filter(t => t.id === schedule.get('trainers')?.value).map(t => ({ id: t.id, name: t.name }));
      const drArray = this.getDateRangesArray(index);
      ts.dateRanges = drArray.controls.map((ctrl: AbstractControl) => {
        const rg = ctrl as FormGroup;
        return {
          id: this.generateId(),
          startDate: rg.get('startDate')?.value,
          endDate: rg.get('endDate')?.value,
          startTime: rg.get('startTime')?.value,
          endTime: rg.get('endTime')?.value,
        };
      });
      ts.trainingDetail = schedule.get('trainingDetail')?.value;
      ts.trainersRemarks = schedule.get('trainersRemarks')?.value;
      ts.resourceTemplateIds = schedule.get('resourceTemplate')?.value ? [{ id: schedule.get('resourceTemplate')?.value, name: '', resourceType: schedule.get('resourceType')?.value }] : [];
      ts.removalOption = schedule.get('deletionType')?.value;
      ts.removalDateTime = schedule.get('deletionDate')?.value;
      ts.accessViaInvitedEmailOnly = schedule.get('accessViaInvitedEmailOnly')?.value;

      this.pluginData.TrainingSchedulesData.push(ts);
    });

    return this.pluginData;
  }


  getPluginData_old(): BasePluginData {

    const formValue = this.requisitionForm.value;
    formValue.trainingSchedules.map((sched: any, index: number) => {
      const ts = new TrainingScheduleData();
      // Get the first schedule (or the active one, if you support multiple)
      const schedule = this.schedules.at(index)?.value;

      if (schedule) {
        this.logger.i('getPluginData :: Training Schedule:', schedule);
        ts.title = schedule.title;
        // Fix: Ensure pluginData.course is never undefined (use null or fallback)
        ts.course = this.courseService.getCourseById(this.defaultCustomCourseId) || new CourseEntity();//(schedule.courseId) || new CourseEntity();
        ts.participants = (schedule.participants || '').split(',').map((p: string) => p.trim()).filter((p: string) => p);
        ts.trainers = (Array.isArray(schedule.trainers) ? schedule.trainers : [schedule.trainers])
          .filter((id: string) => !!id)
          .map((id: string) => {
            const trainer = this.trainers.find(t => t.id === id);
            return trainer ? { id: trainer.id, name: trainer.name } : null;
          })
          .filter((t: any) => t);
        // ts.dateRanges = this.tabCalendarStates[this.activeTabIndex].dateRanges || []//this.tabDateRanges[this.activeTabIndex] || [];
        ts.dateRanges = this.tabCalendarStates[index]?.dateRanges || [];

        // ts.resourceType = schedule.resourceType;
        ts.removalOption = schedule.deletionType;
        ts.removalDateTime = schedule.deletionDate ? new Date(schedule.deletionDate) : undefined;
        ts.trainersRemarks = schedule.trainersRemarks;
        ts.trainingDetail = schedule.trainingDetail;
        if (schedule.resourceType) {
          const selectedResourceTemplate = this.resourceTemplatesByType[schedule.resourceType].find(t => t.id === schedule.resourceTemplate);
          if (selectedResourceTemplate) {
            const prepared = { id: selectedResourceTemplate!.id, name: selectedResourceTemplate!.name, resourceType: schedule.resourceType };
            ts.resourceTemplateIds.push(prepared);
          }
        }
        ts.accessViaInvitedEmailOnly = schedule.accessViaInvitedEmailOnly;
        // ...add any other fields you want to preview
      }

      this.pluginData.TrainingSchedulesData.push(ts);
    });

    this.logger.i('net get PluginData ::Training Schedules Data:', this.pluginData.TrainingSchedulesData);
    return this.pluginData;
  }

  setPluginData_old(data: BasePluginData): void {
    if (data) {
      const incoming = data as TrainingSchedulesData;

      // this.pluginData.TrainingSchedulesData= data;
      incoming.TrainingSchedulesData.map((sched: any, index: number) => {

        // Defensive: always assign a new object to avoid reference issues
        sched.title = incoming.TrainingSchedulesData[index].title || '';
        sched.course = incoming.TrainingSchedulesData[index].course || new CourseEntity();
        // Participants: handle both string[] and comma-separated string
        if (Array.isArray(incoming.TrainingSchedulesData[index].participants)) {
          sched.participants = incoming.TrainingSchedulesData[index].participants.filter(p => !!p);
        } else if (typeof (incoming.TrainingSchedulesData[index].participants as any) === 'string') {
          sched.participants = (incoming.TrainingSchedulesData[index].participants as any).split(',').map((p: string) => p.trim()).filter((p: string) => p);
        } else {
          sched.participants = [];
        }
        // Trainers: handle both Pair[] and string[] of ids
        if (Array.isArray(incoming.TrainingSchedulesData[index].trainers) && incoming.TrainingSchedulesData[index].trainers.length && typeof incoming.TrainingSchedulesData[index].trainers[0] === 'object') {
          sched.trainers = incoming.TrainingSchedulesData[index].trainers as any;
        } else if (Array.isArray(incoming.TrainingSchedulesData[index].trainers)) {
          sched.trainers = (incoming.TrainingSchedulesData[index].trainers as any[])
            .map((id: string) => {
              const trainer = this.trainers.find(t => t.id === id);
              return trainer ? { id: trainer.id, name: trainer.name } : null;
            })
            .filter((t: any): t is { id: string; name: string } => t !== null);
        } else if (typeof (incoming.TrainingSchedulesData[index].trainers as any) === 'string') {
          sched.trainers = (incoming.TrainingSchedulesData[index].trainers as any)
            .split(',')
            .map((id: string) => {
              const trainer = this.trainers.find(t => t.id === id);
              return trainer ? { id: trainer.id, name: trainer.name } : null;
            })
            .filter((t: any): t is { id: string; name: string } => t !== null);
        } else {
          sched.trainers = [];
        }
        // Date ranges
        sched.dateRanges = Array.isArray(incoming.TrainingSchedulesData[index].dateRanges) ? incoming.TrainingSchedulesData[index].dateRanges : [];
        // sched.resourceType = incoming.TrainingSchedulesData[index].resourceTemplateIds[0].resourceType || '';
        sched.removalOption = incoming.TrainingSchedulesData[index].removalOption || '';
        sched.removalDateTime = incoming.TrainingSchedulesData[index].removalDateTime ? new Date(incoming.TrainingSchedulesData[index].removalDateTime) : undefined;
        sched.endDate = incoming.TrainingSchedulesData[index].endDate ? new Date(incoming.TrainingSchedulesData[index].endDate) : undefined;
        sched.trainersRemarks = incoming.TrainingSchedulesData[index].trainersRemarks || '';
        sched.trainingDetail = incoming.TrainingSchedulesData[index].trainingDetail || '';
        sched.resourceTemplateIds = incoming.TrainingSchedulesData[index].resourceTemplateIds || [];
        sched.accessViaInvitedEmailOnly = incoming.TrainingSchedulesData[index].accessViaInvitedEmailOnly || '';
        // Optionally update the form if needed (not shown here)
        //this.pluginData.TrainingSchedulesData.push(sched);
      });

    }
  }
  isPastDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // ignore time
    return date < today;
  }

  setPluginData(data: BasePluginData): void {
    if (!data) return;
    if (!this.requisitionForm) {
      // Delay execution until ngOnInit has created the form
      setTimeout(() => this.setPluginData(data), 50);
      return;
    }
    const incoming = data as TrainingSchedulesData;
    this.pluginData = incoming;

    // Clear old form controls
    const schedulesArray = new FormArray<FormGroup>([]);
    this.tabCalendarStates = {}; // Clear calendar state

    incoming.TrainingSchedulesData.forEach((sched, index) => {
      const group = this.createScheduleGroup();
      group.patchValue({
        title: sched.title || '',
        courseId: this.defaultCustomCourseId,//sched.course?.id || '',
        participants: sched.participants?.join(',') || '',
        trainers: Array.isArray(sched.trainers) && sched.trainers.length > 0 ? sched.trainers[0].id : '',
        trainingDetail: sched.trainingDetail || '',
        trainersRemarks: sched.trainersRemarks || '',
        deletionType: sched.removalOption || 'immediate',
        deletionDate: sched.removalDateTime || '',
        accessViaInvitedEmailOnly: sched.accessViaInvitedEmailOnly || false,
        resourceType: sched.resourceTemplateIds?.[0]?.resourceType || '',
        resourceTemplate: sched.resourceTemplateIds?.[0]?.id || ''
      });

      schedulesArray.push(group);

      // Populate dateRanges FormArray from incoming data
      const drArray = group.get('dateRanges') as FormArray;
      drArray.clear();
      (sched.dateRanges || []).forEach((dr: any) => {
        drArray.push(this.createDateRangeGroup({
          startDate: dr.startDate ? new Date(dr.startDate) : null,
          endDate: dr.endDate ? new Date(dr.endDate) : null,
          startTime: dr.startTime || '09:00',
          endTime: dr.endTime || '17:00',
        }));
      });
      if (drArray.length === 0) {
        drArray.push(this.createDateRangeGroup());
      }
    });

    this.requisitionForm.setControl('trainingSchedules', schedulesArray);
    this.activeTabIndex = 0;
  }


  onRemovalDateChanged(event: any, scheduleIndex: number): void {
    const value = event.target.value ? new Date(event.target.value) : null;

    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    schedule.patchValue({ deletionDate: value });

    // keep pluginData in sync
    if (!this.pluginData.TrainingSchedulesData[scheduleIndex]) {
      this.pluginData.TrainingSchedulesData[scheduleIndex] = {} as any;
    }
    this.pluginData.TrainingSchedulesData[scheduleIndex].removalDateTime = value || undefined;

    this.validateRemovalDate();
  }


  setRemovalOption(option: 'immediate' | 'scheduled' | 'manual'): void {
    const schedule = this.schedules.at(this.activeTabIndex) as FormGroup;

    schedule.patchValue({ deletionType: option });

    if (!this.pluginData.TrainingSchedulesData[this.activeTabIndex]) {
      this.pluginData.TrainingSchedulesData[this.activeTabIndex] = {} as any;
    }
    this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalOption = option;

    if (option === 'scheduled') {
      const defaultRemovalDate = this.minRemovalDate;
      schedule.patchValue({ deletionDate: defaultRemovalDate });
      this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime = defaultRemovalDate;
    } else {
      schedule.patchValue({ deletionDate: null });
      this.pluginData.TrainingSchedulesData[this.activeTabIndex].removalDateTime = undefined;
    }
  }


}
